<template>
  <main id="mainContent" class="payment">
    <template v-if="isModify">
      <div class="app-main-content">
        <p>Unauthorized</p>
      </div>
    </template>
    <template v-else>
      <PaymentAlert :hasError="ftHasPaymentError" :errorMessage="ftErrorMessage" />
      <Stepper :step="stepNum" />
      <Timer ref="timer" />
      <div class="app-main-content">
        <h1>{{ screenData.title }}</h1>
        <p>{{ screenData.description }}</p>
        <div class="app-content-panels"><!-- prevent form stretching to full width -->
          <form class="app-panel-left" @submit="handleFormSubmit">
            <p class="app-required-indicator __before">Required field</p>
            <!-- CC #  -->
            <div class="app-input-row">
              <!-- Replaced with Vee-Validate v4 Field component -->
              <Field v-slot="{ errors, field }" name="Credit card number" :rules="ccNumberRules">
                <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                  <label for="ccNumber" class="app-required-indicator __after">Credit card (Numbers only)</label>
                  <div class="cc-img">
                    <input v-bind="field" type="text" id="ccNumber" v-model="myCardNumber" aria-describedby="ccNumberDesc" />
                    <span class="accepted-cards">
                      <img src="@ridmv/web-themes/images/icons/img_visa.svg" alt="VISA">
                      <img src="@ridmv/web-themes/images/icons/img_master.svg" alt="MasterCard">
                      <img src="@ridmv/web-themes/images/icons/img_discover.svg" alt="Discover">
                      <img src="@ridmv/web-themes/images/icons/img_amex.svg" alt="American Express">
                    </span>
                  </div>
                  <p class="error-message">{{ errors[0] }}</p>
                  <p id="ccNumberDesc" class="sr-only">
                    Required. Accepted cards: VISA, MasterCard, Discover or American Express.
                  </p>
                </div>
              </Field>
            </div>
            <div class="app-input-row">
              <!-- exp date -->
              <div class="expiration-container">
                <div class="app-label-non-control app-required-indicator __after">Expiration date (MM/YYYY)</div>
                <div class="combo-inputs">
                  <Field v-slot="{ errors, field }" name="Month" :rules="monthRules">
                    <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                      <label for="expireMonth" class="app-required-indicator __after sr-only">Expiration month</label>
                      <input v-bind="field" type="text" id="expireMonth" class="exp-month" ref="expireMonth"
                       v-model="myExpireMonth" aria-describedby="expireDateDesc" @keyup="switchFocus" />
                      <p hidden>{{ expMonthError = errors[0] || '' }}</p>
                    </div>
                  </Field>
                  <span>/</span>
                  <Field v-slot="{ errors, field }" name="Year" :rules="yearRules">
                    <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                      <label for="expireYear" class="app-required-indicator __after sr-only">Expiration year</label>
                      <input v-bind="field" type="text" id="expireYear" class="exp-year" ref="expireYearInputRef" v-model="myExpireYear" aria-describedby="expireDateDesc" />
                      <p hidden>{{ expYearError = errors[0] || '' }}</p>
                    </div>
                  </Field>
                </div><!-- combo-inputs -->

                <!-- one-off due to mm-yyyy combo and limitation on validation lib's dependencies -->
                <p class="error-message">{{ expDateErrors }}</p>
                <p id="expireDateDesc" class="sr-only">Required. Two-digit month and four-digit year format.</p>
              </div><!-- /expiration-container -->

              <!-- CVV -->
              <Field v-slot="{ errors, field }" name="CVV" :rules="cvvRules">
                <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                  <label for="cvv" class="app-required-indicator __after">
                    CVV
                    <Tooltip theme="my-custom-tooltip" :triggers="['hover', 'click', 'focus']">
                      <button type="button" class="btn-tooltip-trigger" aria-label="CVV info">
                        <img src="@ridmv/web-themes/images/icons/question-circle-solid.svg" alt="Description" class="cvv-tooltip">
                      </button>
                      <template #popper>
                        {{ cvvDescription }}
                        <span class="sr-only">{{ cvvDescription }}</span>
                      </template>
                    </Tooltip>
                  </label>
                  <input v-bind="field" type="text" id="cvv" class="cvv" v-model="myCvv" aria-describedby="cvvDesc" />
                  <p>{{ errors[0] }}</p>
                  <p id="cvvDesc" class="sr-only">Required. Three-digit format.</p>
                </div>
              </Field>
            </div>
            <div class="app-input-row">
              <!-- first name on card -->
              <Field v-slot="{ errors, field }" name="First name" :rules="firstNameRules">
                <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                  <label for="firstName" class="app-required-indicator __after">First name</label>
                  <input v-bind="field" type="text" id="firstName" v-model="myFirstName" aria-describedby="firstNameDesc" />
                  <p>{{ errors[0] }}</p>
                  <p id="firstNameDesc" class="sr-only">Required.</p>
                </div>
              </Field>

              <!-- last name on card -->
              <Field v-slot="{ errors, field }" name="Last name" :rules="lastNameRules">
                <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                  <label for="lastName" class="app-required-indicator __after">Last name</label>
                  <input v-bind="field" type="text" id="lastName" v-model="myLastName" aria-describedby="lastNameDesc" />
                  <p>{{ errors[0] }}</p>
                  <p id="lastNameDesc" class="sr-only">Required.</p>
                </div>
              </Field>
            </div>

            <!-- company name on card -->
            <Field v-slot="{ errors, field }" name="Organization name" :rules="companyNameRules">
              <div class="app-input-group" :class="{ 'app-invalid-group': errors.length > 0 }">
                <label for="companyName" style="padding-top: 0.2em">Company name</label>
                <input v-bind="field" type="text" id="companyName" class="co" v-model="myCompanyName" 
                  aria-describedby="companyNameDesc"
                />
                <p style="width: unset">{{ errors[0] }}</p>
                <p id="companyNameDesc" class="sr-only">Optional.</p>
              </div>
            </Field>

            <TotalBill :totalBill="myBill" />

            <BaseButtonGroup>
              <button type="button" @click="handleRouting(true)">{{ language.btnPrev }}</button>
              <button type="submit" class="app-btn-primary" 
                :disabled="!myBill.totalSumFeeAmount" @click.prevent="handleRouting(false)">
                {{ language.btnPayAndConfirmRes }}
              </button>
            </BaseButtonGroup>
          </form>
        </div>
      </div>
      <OneTimeToken :card-number="myCardNumber" 
                    :card-month="myExpireMonth" 
                    :card-year="myExpireYear" 
                    :card-cvv="myCvv" 
                    :fetch-token="fetchToken"
                    @doPay="parseForteResponse"
      />
    </template>
  </main>
</template>


<script setup>
import { ref, computed, watch, onMounted, nextTick, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { loadScript } from 'vue-plugin-load-script'
import { zonedTimeToUtc } from 'date-fns-tz';
import { useForm, Field } from 'vee-validate';
import appConfig from '@/app.config';
import Stepper from '@/components/reservation/Stepper';
import Timer from '@/components/reservation/Timer';
import PaymentAlert from '@/components/reservation/payment/PaymentAlert';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import TotalBill from '@/components/reservation/payment/TotalBill';
import OneTimeToken from '@/components/reservation/payment/OneTimeToken';
import getForteConfig from '@/components/reservation/payment/forteConfig';
import { PAYMENT_GET_FEES } from '@/store/_commons';
import { SLOTS_HOLD_CREATE } from '../store/_commons';
import { stripExtraSpaces } from '@/utils/utils';
import { Tooltip } from 'floating-vue';
import { useHead } from '@unhead/vue';

useHead({
  title: `Payment | ${appConfig.shortName}`,
  meta: [
    {
      name: 'description',
      content: 'Pre-payment for the selected service is required.'
    }
  ]
});

// Vuex store and route
const store = useStore();
const router = useRouter();

const { validate, handleSubmit } = useForm();

// Reactive state
const timer = ref(null);
const expireMonth = ref(null);

const stepNum = ref(4);
const screenId = ref(10);
const screenData = ref({});

const ftToken = ref(undefined);
const ftHasPaymentError = ref(false);
const ftErrorMessage = ref('');
const fetchToken = ref(false);

const cvvDescription = ref('For Discover, MasterCard and Visa, enter last three digits on the signature strip. For American Express, enter the four digits in small print on the front of the card.');
const myCardNumber = ref('');
const myExpireYear = ref('');
const myExpireMonth = ref('');
const myExpireDate = ref('');
const myCvv = ref('');
const myFirstName = ref('');
const myLastName = ref('');
const myCompanyName = ref('');

const myBill = ref({});               // from api call
const myPaymentDate = ref(undefined); // capture this for edge case, e.g. user makes res close to midnight

const expMonthError = ref('');
const expYearError = ref('');
const isModify = ref(false);

const supportedCardTypes = ref([ 'amex', 'disc', 'mast', 'visa' ]);    // both individual + corporate

const expireYearInputRef = ref(null); 

// Computed Properties 
const language = computed(() => store.state.user.LANG);

const ccNumberRules = computed(() => ({
  fieldRequired: true,
  numeric: true,
  minMax: [13, 19],
}));

const cvvRules = computed(() => ({
  fieldRequired: true,
  numeric: true,
  minMax: [3, 4],
}));

const monthRules = computed(() => ({
  fieldRequired: true,
  numeric: true,
  max: 2,
  monthBetween: [1, 12],
}));

const yearRules = computed(() => ({
  fieldRequired: true,
  numeric: true,
  max: 4,
  validYear: [ 20 ],
}));

// Combined expiration date errors
const expDateErrors = computed(() => {
  return `${expMonthError.value} ${expYearError.value}`;
});

const PAGES = computed(() => store.state.user.PAGES);
const STEPS = computed(() => store.state.user.STEPS);
const resDetails = computed(() => store.state.user.resDetails);
const reservationSlotId = computed(() => store.state.user.reservationSlotId);
const resAppointmentBarcode = computed(() => store.state.user.resAppointmentBarcode);
const resSlotHoldEndTime = computed(() => store.state.user.resSlotHoldEndTime);
const resTypeForCustomer = computed(() => store.state.user.resTypeForCustomer);

const firstNameRules = (value) => {
  if (!value) {
    return 'First name is required.';
  }
  if (!/^[a-zA-Z0-9!@#$%^&*()_+=[\]{}\u2018\u2019':"\\.>/?`~ ]*$/.test(value)) { // Allows letters, spaces, hyphens, and periods
    return 'Special characters ";", "<", "|", "," and "-" are not allowed.' + 'For hyphenated names please use a space in place of hyphen / dash.';
  }
  if (value.length > 40) {
    return 'First name is limited to 40 characters.';
  }
  return true;
};

const lastNameRules = (value) => {
  if (!value) {
    return 'Last name is required';
  }
  if (!/^[a-zA-Z0-9!@#$%^&*()_+=[\]{}\u2018\u2019':"\\.>/?`~ ]*$/.test(value)) { // Allows letters, spaces, hyphens, and periods
    return 'Special characters ";", "<", "|", "," and "-" are not allowed.' + 'For hyphenated names please use a space in place of hyphen / dash.';
  }
  if (value.length > 40) {
    return 'Last name is limited to 40 characters.';
  }
  return true;
};

// Custom rule for company name (optional field)
const companyNameRules = (value) => {
  // If the field is empty, it's valid because it's optional
  if (!value) {
    return true; 
  }
  // Check for special characters (adjust regex as needed)
  if (!/^[a-zA-Z0-9!@#$%^&*()_+=[\]{}\u2018\u2019':"\\.>/?`~ ]*$/.test(value)) { // Allows letters, spaces, hyphens, and periods
    return 'Special characters ";", "<", "|", "," and "-" are not allowed.' + 'For hyphenated names please use a space in place of hyphen / dash.';
  }
  // Check for max length
  if (value.length > 80) {
    return 'Organization name is limited to 80 characters.';
  }
  return true;
};

watch(resAppointmentBarcode, (newValue) => {
  // route to next screen when reservation creation is successful
  if (newValue && newValue.length > 0) {
    router.push({ name: 'ReservationFinal' });
  }
});

onMounted(async () => {
  if (reservationSlotId.value > 0) {
    screenData.value = STEPS.value[screenId.value];
    await setBill();
    await loadForteJs();
  } else {
    router.push({ name: PAGES.value.userInfo });
  }

  isModify.value = await store.dispatch('user/isReservationForUpdate');

  // for developer only
  setLocalhostValues();
});

// Lifecycle hook
/**
 * IMPORTANT: Originally set to unload Forte script before component is destroyed. BUT if user happens to re-use
 * the same session, e.g. going from Payment screen back a few steps to pick a different time slot, the Forte object
 * initially created will still be hanging around. That is, unloading the Forte script does NOT destroy the Forte
 * object already instantiated. So, reloading the Forte script will create a _different_ Forte instance which
 * eventually leads to multiple payment calls (and reservation creations) based on the number of Forte objects
 * in memory. These Forte instances are not accessible directly.
 *
 * Cricket from Forte support on nullifying the Forte object(s)... Did not find any way within Forte script to
 * destroy an existing instance so not calling the unloading script here as a less-than-optimal solution.
 */
onBeforeUnmount(() => {
  resetData();
});

// Methods
const createSlotHold = (isExtendSlot) => store.dispatch(SLOTS_HOLD_CREATE, isExtendSlot);
const getFees = () => store.dispatch(PAYMENT_GET_FEES);

const handleRouting = async (isPrev) => {
  if (isPrev) {
    await router.push({ name: PAGES.value.confirmRes });
    return;
  }

  const valid = await isFormValid();
  if (!valid) {
    return;
  }

  // signal child component to get OTT IFF all form fields validated
  // Add additional 30 sec to the hold time to allow for payment processing, if current time is less than 30 sec
  const end = resSlotHoldEndTime.value;
  const now = new Date().getTime();
  if (now + 30000 > end) {
    store.state.user.resSlotHoldEndTime = end + 30000;
    await createSlotHold(true);
  }

  fetchToken.value = true;
};

/**
 * Starts Forte charging process.
 * @returns {Promise<void>}
 */
const makePayment = async () => {
  const ACTION = 'sale';
  const paidDate = getUtcDate(myPaymentDate.value);

  try {
    const args = {
      onlineReservationCreateReservationRequest: {
        reservationSlotId: reservationSlotId.value,
        reservationUserIdentifier: resDetails.value.reservationUserId,
        // visitTypeId: resDetails.value.visitTypeId,    // TODO: replace with resTypeForCustomer.visitTypeId
        // isFeeApplicable: resDetails.value.isFeeApplicable,  // TODO: replace with resTypeForCustomer.isFeeApplicable
        visitTypeId: resTypeForCustomer.value.visitTypeId,    // TODO: replace with resTypeForCustomer.visitTypeId
        isFeeApplicable: resTypeForCustomer.value.isFeeApplicable,
        onlineReservationFeeDetail: {
          authorizationAmount: myBill.value.totalSumFeeAmount,
          feeName: myBill.value.rimsFeeName,
          feePaidDate: paidDate,
          paymentMakerCompanyName: stripExtraSpaces(myCompanyName.value),
          paymentMakerFirstName: stripExtraSpaces(myFirstName.value),
          paymentMakerLastName: stripExtraSpaces(myLastName.value),
          serviceFeeAmount: myBill.value.forteServiceFee,
          technologyFeeAmount: myBill.value.rimsTechnologyFeeAmount,
          transactionFeeAmount: myBill.value.rimsTransactionFeeAmount,
        },
      },
      PaymentRequest: {
        action: ACTION,
        subtotalAmount: myBill.value.rimsTotalFeeAmount,
        serviceFeeAmount: myBill.value.forteServiceFee,
        authorizationAmount: myBill.value.totalSumFeeAmount,
        billingAddress: {
          firstName: stripExtraSpaces(myFirstName.value),
          lastName: stripExtraSpaces(myLastName.value),
        },
        card: {
          oneTimeToken: ftToken.value,
        },
      },
    };
    // Payment is only applicable for new reservations - no update allowed
    await store.dispatch('reservation/createReservation', args); 
  } catch (err) {
    ftHasPaymentError.value = true;
    console.error('Forte payment failed...', err);
  }
};

/**
 * Converts local/client datetime to UTC and send it back to RIMS.
 * Note: RIMS persists all date(time) in database in ET timezone. Attempted to pre-convert UTC to EST
 * for RIMS... But it appears Chrome (v98) got confused in the process. Decided to send UTC along as
 * it's the standard. RIMS will have to deal with the datetime conversion on the backend.
 * @returns {Date}
*/
const getUtcDate = (dt) => {
  // [1] get client local datetime AND timezone
  const clientDate = dt || new Date();
  const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // [2] convert client local datetime to UTC (must specify local timezone)
  const utcDate = zonedTimeToUtc(clientDate, clientTimezone);
  // [3] send UTC datetime to RIMS (browser will get confused if UTC is converted to ET before sending... 😖)
  return utcDate.toISOString();
};

/**
 * Checks card type returned by Forte to ensure it's one of the business-predefined supported cards.
 * Note:
 *    Forte will always return 'success' and OTT even if card type is NOT supported.
 *    Forte OTT will return an error if:
 *      (1) The card type does not match the card number
 *      (2) The card is expired
 *      (3) The card number format is incorrect (including cvv)
 *      (4) Any required field is missing
 * @param type The card_type value from Forte OTT response.
 */
const isCardTypeSupported = (type) => {
  // Access the array's value using .value in Vue 3
  return supportedCardTypes.value.find(item => item === type);
};

/**
 * Validates all form fields before calling Forte services.
 * @returns {Promise<Boolean>}
 */
const isFormValid = async () => {
  // Use the validate() function provided by useForm
  const { valid } = await validate();
  return valid;
};

// Example of how to use isFormValid in a submission handler
const handleFormSubmit = handleSubmit(async (values) => {
  // This function is only executed if the entire form is valid
  console.log('Form is valid!', values);
  // Call your forte tokenization logic or set fetchToken.value = true here
});

/**
 * Ensures Forte response _really really_ is good to go before processing payment.
 * @param data
 */
const parseForteResponse = (data) => {
  const res = data.forte;

  if (data.hasOtt) {
    // forte OTT fetch succeeds... but is the OTT _really_ good for payment?
    verifyOtt(res);
  } else {
    // forte OTT fetch fails
    setPaymentAlert(parseOttError(res.response_description));
  }

  // always reset for the next round
  fetchToken.value = false;
};

/**
 * Converts Forte OTT response description to proper English.
 * @param desc
 * @returns {string|*}
 */
const parseOttError = (desc) => {
  if (desc && desc.endsWith('invalid.')) {
    let msg = desc.replace('cvv', 'CVV');
    msg = msg.replace('expire_month and expire_year', 'expiration month and/or expiration year');
    msg = msg.split('_').join(' ');   // replaceAll won't work on older iOS
    return msg;
  }
  return '';
};

/**
 * Confirms that the OTT returned by Forte contains a supported card type before making payment.
 * @param res
 */
const verifyOtt = (res) => {
  const supported = isCardTypeSupported(res.card_type);
  // make payment IFF OTT returns a supported card type
  // if (!supported) {
  if (supported === undefined || supported === false) {   
    setPaymentAlert('Sorry, we only support VISA, MasterCard, Discover and American Express.');
  } else {
    // remove existing alert banner, if any
    ftHasPaymentError.value = false;
    ftErrorMessage.value = '';
    ftToken.value = res.onetime_token;
    makePayment();
  }
};

/**
 * Resets Forte data just in case.
 */
const resetData = () => {
  ftToken.value = '';
  ftHasPaymentError.value = false;
  ftErrorMessage.value = '';
  console.log('Resetting payment data.');
};

/**
 * Gets fee details from backend.
 * @returns {Promise<void>}
 */
const setBill = async () => {
  const fees = await getFees();
  if (fees) {
    myBill.value = fees.data;
  }
};

const setPaymentAlert = (msg) => {
  ftToken.value = '';  // MUST reset OTT or payment will repeat based on # of errors prior to form submit
  ftHasPaymentError.value = true;
  ftErrorMessage.value = msg;
};

/**
 * Jumps from MM to YYYY.
 * @param e
 */
const switchFocus = async (e) => {
  const isTab = e.key === 'Tab'; 
  const isShift = e.key === 'Shift';

  if (!isTab && !isShift) {
    // Get the ID of the element that triggered the event
    const targetId = e.target.id;
    const targetLength = e.target.value.length;
    // Check if the event came from the 'expireMonth' input and its length is 2
    if (targetId === 'expireMonth' && targetLength === 2) {
      // Use nextTick to ensure the DOM is updated before focusing
      nextTick(() => { 
        // Focus the expireYear input, ensuring the ref has a value
        expireYearInputRef.value?.focus(); // Use optional chaining for safety
      });
    }
  }
};

/**
 * Loads Forte JS library only when needed.
 * For some reason, the script MUST be loaded here instead of inside OneTimeToken component.
 */
const loadForteJs = async () => {
  const config = getForteConfig(); // Call the function to get the config
  console.log('forte js to be loaded...', config.url);

  try {
    await loadScript(config.url);
    // don't call getFees here to avoid dependency
  } catch (error) {
    // this should catch any network call error (no requirements specified)
    console.error(error);
  }
};

/**
 * Sets some fake values because there are too many fields to fill for development!
 */
const setLocalhostValues = () => {
  if ((window.location.host).indexOf('localhost') > -1) {
    myCardNumber.value = '4111111111111111'; //'5555 5555 5555 7462'
    myExpireYear.value = '2033';
    myExpireMonth.value = '08';
    myExpireDate.value = '12/2027';
    myCvv.value = '000';
    myFirstName.value = 'Tony';
    myLastName.value = 'Stark';
    myCompanyName.value = 'Test Co. Ltd.';
  }
};

</script>


<style lang="scss" scoped>
.payment {
  input {
    &.exp-month, &.exp-year, &.cvv {
      text-align: center;
    }
    &.co {
      width: 100%;
    }
    &.exp-month {
      max-width: 3.5em;
    }
    &.exp-year, &.cvv {
      max-width: 5em;
    }
  }

  .cc-img {
    display: flex;
    flex-wrap: wrap;
    #ccNumber {
      margin-right: 0.75em;
    }
    .accepted-cards {
      display: flex;
      gap: 0.5em;
      img {
        margin-top: 0.15em; // for wrapped images on small screens
        width: 39px;
      }
    }
    // 🤬 too many one-offs in the design of this app...
    ~ .error-message {
      color: var(--ri-red);
      @extend %font-sans-regular;
      padding-top: 0.5em;
    }
  }

  .expiration-container {
    // could limit container width, if needed
    .combo-inputs {
      display: flex;
      gap: 0.35em;
      span:nth-child(2) {
        padding-top: 0.65em;
      }
    }
  }

  .error-message {
    color: var(--ri-red);
    line-height: 1.35em;
    padding-top: unset;
    padding-bottom: 1.25em;
  }

  .app-required-indicator {
    display: flex;
    gap: 0.5rem;
    .btn-tooltip-trigger{
      min-width: 25px;
      border: none;
      background-color: unset;
      border-radius: unset;
      padding: 0rem;
      .cvv-tooltip{
        width: 20px;
      }
    }
  }
}

button {
  margin-bottom: 1.125em;
  &:not(:last-child) {
    margin-right: 1em;
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}
</style>
